import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';

import Map from 'ol/Map';
import View from 'ol/View';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Group from 'ol/layer/Group';
import Overlay from 'ol/Overlay';
import { Style, Icon } from 'ol/style';
import { fromLonLat } from 'ol/proj';
import { apply } from 'ol-mapbox-style';

import { ClockMapMarker } from '../../models/map-marker';

const ICON_SRC: Record<ClockMapMarker['iconType'], string> = {
  clock: '/assets/clock-marker.png',
  construction: '/assets/construction-marker.png'
};

@Component({
  selector: 'app-clock-map',
  standalone: true,
  imports: [CommonModule, DxButtonModule],
  templateUrl: './clock-map.component.html',
  styleUrls: ['./clock-map.component.scss']
})
export class ClockMapComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() markers: ClockMapMarker[] = [];

  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('popup', { static: false }) popup!: ElementRef<HTMLElement>;
  @ViewChild('popupContent', { static: false }) popupContent!: ElementRef<HTMLElement>;

  private map?: Map;
  private overlay?: Overlay;
  private mapInitialized = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markers']) {
      this.tryInit();
    }
  }

  ngAfterViewInit(): void {
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.map?.setTarget(undefined);
    this.map = undefined;
    this.overlay = undefined;
  }

  closePopup(ev: Event): void {
    ev.preventDefault();
    this.overlay?.setPosition(undefined);
  }

  openGoogleMaps(): void {
    const point = this.primaryMarker();
    if (!point) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${point.latitude},${point.longitude}`;
    window.open(url, '_blank', 'noopener');
  }

  openStreetView(): void {
    const point = this.primaryMarker();
    if (!point) return;
    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${point.latitude},${point.longitude}`;
    window.open(url, '_blank', 'noopener');
  }

  private primaryMarker(): ClockMapMarker | undefined {
    return this.markers.find(m => m.iconType === 'clock') ?? this.markers[0];
  }

  private tryInit(): void {
    if (this.mapInitialized) return;
    if (!this.markers || this.markers.length === 0) return;
    if (!this.mapContainer) return;
    setTimeout(() => this.initMap(), 0);
  }

  private initMap(): void {
    if (this.mapInitialized) return;
    this.mapInitialized = true;

    const vectorSource = new VectorSource();
    for (const m of this.markers) {
      const feat = new Feature({
        geometry: new Point(fromLonLat([m.longitude, m.latitude])),
        descr: m.description
      });
      feat.setStyle(new Style({
        image: new Icon({
          anchor: [0.5, 1],
          crossOrigin: 'anonymous',
          src: ICON_SRC[m.iconType]
        })
      }));
      vectorSource.addFeature(feat);
    }

    this.overlay = new Overlay({
      element: this.popup.nativeElement,
      autoPan: { animation: { duration: 250 } }
    });

    const baseGroup = new Group();

    this.map = new Map({
      target: this.mapContainer.nativeElement,
      layers: [baseGroup],
      view: new View({
        center: fromLonLat([12.57, 44.06]),
        zoom: 13,
        maxZoom: 22
      }),
      overlays: [this.overlay]
    });

    this.map.on('singleclick', (evt) => {
      const feature = this.map!.forEachFeatureAtPixel(evt.pixel, (f) => f);
      if (!feature) {
        this.overlay!.setPosition(undefined);
        return;
      }
      const geometry = feature.getGeometry();
      if (geometry && geometry.getType() === 'Point') {
        const coords = (geometry as Point).getCoordinates();
        const descr = feature.get('descr') || 'Nessuna descrizione';
        this.popupContent.nativeElement.innerHTML = descr;
        this.overlay!.setPosition(coords);
      } else {
        this.overlay!.setPosition(undefined);
      }
    });

    apply(this.map, 'https://tiles.openfreemap.org/styles/liberty')
      .then(() => {
        const vectorLayer = new VectorLayer({ source: vectorSource });
        this.map!.addLayer(vectorLayer);
        if (vectorSource.getFeatures().length > 0) {
          const extent = vectorSource.getExtent();
          if (extent) {
            this.map!.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 22, duration: 800 });
          }
        }
      })
      .catch(err => {
        console.warn('Errore apply map style:', err);
        const vectorLayer = new VectorLayer({ source: vectorSource });
        this.map!.addLayer(vectorLayer);
      });
  }
}
